import { FC, ForwardedRef, forwardRef, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  ParagraphExtension,
  BoldExtension,
  ItalicExtension,
  UnderlineExtension,
  FontSizeExtension,
  TextColorExtension,
  NodeFormattingExtension,
} from 'remirror/extensions';
import {
  Remirror,
  EditorComponent,
  Toolbar,
  BasicFormattingButtonGroup,
  TextAlignmentButtonGroup,
  useRemirror,
} from '@remirror/react';
import { ClickAwayListener } from '@mui/material';

import { FontSizePicker } from './components/fontSizePicker.component';
import { ColorPicker } from './components/colorPicker.component';
import 'remirror/styles/all.css';
import { styled } from '@mui/material/styles';
import { prosemirrorNodeToHtml } from 'remirror';
import { EditorState, Transaction } from '@remirror/pm/state';
import { EditorView } from '@remirror/pm/view';
import { Node as ProsemirrorNode } from '@remirror/pm/model';

export const TextEditor: FC<ITextEditorProps> = forwardRef(
  (
    {
      defaultValue,
      onChange,
      onBlur,
      defaultFormat,
      disabled,
      toolbarItems,
      minHeight,
      value: externalValue,
      isUseForm = false,
      noWrapText,
    },
    ref: ForwardedRef<null>,
  ) => {
    const { fontSize: defaultFontSize } = defaultFormat || {};

    // `defaultValue` only seeds the document — at mount (`useRemirror({ content })` below)
    // and for as long as the form holds no value for this field at all (`undefined`).
    // Once the form does hold a value, `null` means "the user emptied it", NOT "fall back
    // to the default": `handleChange` reports `null` for a document with no text, so
    // resolving `null` back to `defaultValue` made emptying the editor re-insert the text
    // that had just been deleted (WA-3052) and replace the document mid-edit (WA-3905).
    const incomingValue = externalValue === undefined ? defaultValue ?? null : externalValue;
    const lastAppliedExternal = useRef<string | null | undefined>(undefined);

    const paragraphExtension = useMemo(
      () =>
        new ParagraphExtension({
          ...(defaultFontSize && {
            extraAttributes: {
              // Add "font size" in style tag when new <p> is added
              // Add/modify "text-align" in style tag corresponding to real alignment
              style: {
                default: () => `font-size: ${defaultFontSize}pt;`,
                toDOM: (attrs, node) => {
                  let parapraphStyle = (attrs.style as string) ?? '';

                  const newParagraphAlignment = node.node?.attrs?.nodeTextAlignment ?? 'left';
                  if (
                    newParagraphAlignment === 'left' ||
                    newParagraphAlignment === 'center' ||
                    newParagraphAlignment === 'right'
                  ) {
                    if (parapraphStyle.indexOf('text-align:') > -1) {
                      parapraphStyle = parapraphStyle.replace(
                        /(text-align:\s*)([^;"]+)/,
                        '$1' + newParagraphAlignment,
                      );
                    } else {
                      parapraphStyle = parapraphStyle + `text-align: ${newParagraphAlignment};`;
                    }
                  }

                  return parapraphStyle;
                },
              },
            },
          }),
        }),
      [defaultFontSize],
    );
    const { state, manager, setState } = useRemirror({
      extensions: () => [
        paragraphExtension,
        new FontSizeExtension({
          ...(defaultFontSize && { defaultSize: defaultFontSize.toString(), unit: 'pt' }),
        }),
        new BoldExtension({}),
        new ItalicExtension(),
        new UnderlineExtension(),
        new TextColorExtension({}),
        new NodeFormattingExtension({}),
      ],
      content: defaultValue,
      selection: 'start',
      stringHandler: 'html',
    });

    const handleChange = useCallback(
      ({ state, tr: transaction }: IChangeArgs) => {
        setState(state);
        if (transaction?.docChanged) {
          onChange?.(reportedValue(state.doc));
        }
      },
      [onChange, setState],
    );

    const value = prosemirrorNodeToHtml(state.doc);
    // What this editor currently reports — the same value `handleChange` last handed
    // to the form, so the effect below can tell "the form already holds what the
    // document says" from a genuine external change. Reuses `value` rather than
    // calling `reportedValue`, to serialize the document only once per render.
    const currentValue = hasText(state.doc) ? value : null;

    useEffect(() => {
      if (!isUseForm) return;

      if (lastAppliedExternal.current === incomingValue) return;

      // Compare what the editor *reports* against what the form holds, rather than
      // the raw serialized HTML. A document with no text serializes to `<p></p>` but
      // is reported as `null`, so a raw comparison read every edit back to empty as
      // an external change and replaced the whole document under the caret — which is
      // what left ProseMirror's view descriptors pointing past the end of the DOM
      // text node (WA-3905 `IndexSizeError` out of `EditorView.focus`).
      if (currentValue !== incomingValue) {
        // A controlled editor (`state` + `onChange` on `<Remirror/>`) must be updated
        // through the `state` prop. Remirror's `setContent` writes straight to
        // `view.updateState` without ever calling `onChange`, so the React state and
        // the view's own state fork permanently — the view renders the new document
        // while `state` still describes the old one, and nothing resyncs them because
        // `useControlledEditor` only reacts to a change of `state` identity.
        setState(manager.createState({ content: incomingValue ?? undefined }));
      }

      lastAppliedExternal.current = incomingValue;
    }, [incomingValue, currentValue, isUseForm, manager, setState]);

    const hasBlured = useRef(false);
    const handleClickOutside = useCallback(() => {
      if (hasBlured.current) {
        // Run handler only on the first click outside (ignore subsequent clicks)
        hasBlured.current = false;
        if (value) {
          onBlur?.(currentValue);
        }
      }
    }, [hasBlured.current, value, currentValue]);

    return (
      <ClickAwayListener onClickAway={handleClickOutside}>
        <SRemirrorBox
          className='remirror-theme'
          onBlur={() => {
            hasBlured.current = true;
          }}
          onFocus={() => {
            hasBlured.current = false;
          }}
          noWrapText={noWrapText}
          {...{ toolbarItems, minHeight, ref, disabled }}
        >
          <Remirror manager={manager} onChange={handleChange} state={state} editable={!disabled}>
            {!disabled && (
              <Toolbar sx={{ backgroundColor: 'transparent', maxHeight: '28px' }}>
                <BasicFormattingButtonGroup />
                {(!toolbarItems || toolbarItems.includes('size')) && (
                  <FontSizePicker
                    fontSizes={FONT_SIZES}
                    defaultFontSize={defaultFontSize?.toString()}
                  />
                )}
                {(!toolbarItems || toolbarItems.includes('color')) && (
                  <ColorPicker colors={FONT_COLORS} />
                )}
                {toolbarItems?.includes?.('alignment') && <TextAlignmentButtonGroup />}
              </Toolbar>
            )}
            <EditorComponent />
          </Remirror>
        </SRemirrorBox>
      </ClickAwayListener>
    );
  },
);

/**
 * Whether a document carries any text, read off the ProseMirror document itself
 * rather than by re-parsing the serialized HTML through
 * `valueHelper.checkValueInHtml`. The two agree for this editor's schema — it emits
 * nothing but `<p>` blocks with inline marks, and `innerText` on a non-rendered
 * element is its `textContent` — but the helper is browser-only (`innerText` needs a
 * layout engine; jsdom always returns `undefined`, see `value.helper.test.ts`) and
 * re-parses the whole document on every keystroke. `checkValueInHtml` stays in use
 * elsewhere, where all that is available is an HTML string read back out of a form.
 */
const hasText = (doc: ProsemirrorNode): boolean => doc.textContent.trim().length > 0;

/**
 * The value this editor reports to its consumer for a document: its HTML, or `null`
 * once the document carries no text — so a document holding nothing but empty or
 * whitespace-only paragraphs is stored as "no value" rather than as stray markup.
 *
 * The external-value sync effect compares through this same rule, so "the form holds
 * `null`" and "the document is empty" are recognised as the *same* value rather than
 * as an external change that has to be written back into the document.
 */
const reportedValue = (doc: ProsemirrorNode): string | null =>
  hasText(doc) ? prosemirrorNodeToHtml(doc) : null;

const FONT_SIZES = ['8', '10', '12', '14', '16', '18', '20', '24', '30'];
// prettier-ignore
const FONT_COLORS = [
	'#000000', '#D32D1F', '#F19D38', '#FFFE54', '#3C8725', '#2B66C5', '#8D41F6',
	'#FFFFFF', '#F3CECD', '#FCECCF', '#FFFED1', '#D1E7CE', '#D0DFF3', '#E7D7FC',
	'#BBBBBB', '#DF6E6A', '#F6C375', '#FFFE7E', '#7AB66F', '#74A2DB', '#B989F8',
	'#888888', '#931C12', '#A86D25', '#B2B138', '#285E17', '#1C48AB', '#632EAB',
	'#444444', '#540C06', '#603F11', '#66651C', '#143609', '#0D2962', '#381962'
]

export interface ITextEditorProps {
  onChange?: (v: string | null) => void;
  onBlur?: (v: string | null) => void; // Fired only in "dirty" state (i.e. if value was changed)
  defaultValue?: string;
  defaultFormat?: {
    fontSize?: number;
  };
  disabled?: boolean;
  toolbarItems?: TToolbar[];
  minHeight?: number;
  /**
   * Controlled value. `undefined` = the form holds nothing for this field yet (the
   * editor keeps `defaultValue`); `null` = the field is set and empty.
   */
  value?: string | null;
  isUseForm?: boolean;
  /** If true, text will not wrap and will be in one line. Helpful for text align controls */
  noWrapText?: boolean;
}

export type TToolbar = 'bold' | 'italic' | 'size' | 'color' | 'underline' | 'alignment';

interface IChangeArgs {
  tr?: Transaction;
  state: EditorState;
  previousState: EditorState;
  internalUpdate: boolean;
  firstRender: boolean;
  view: EditorView;
}

const SRemirrorBox = styled('div')<{
  toolbarItems: ITextEditorProps['toolbarItems'];
  minHeight: ITextEditorProps['minHeight'];
  noWrapText: ITextEditorProps['noWrapText'];
  disabled: ITextEditorProps['disabled'];
}>(({ theme, toolbarItems, minHeight, noWrapText, disabled }) => ({
  '& .ProseMirror': {
    boxShadow: `${theme.palette.grey['400']} 0px 0px 0px 0.06em`,
    lineHeight: 1.3,
    // fontFamily to see the same font like used in reports generation (it is Verdana after html->rtf)
    fontFamily: 'Verdana, Geneva, Tahoma, sans-serif',
    ...(noWrapText && { whiteSpace: 'nowrap' }),
    padding: '4px 8px',
    ...(typeof minHeight === 'number' && { minHeight: `${minHeight}px` }),
    '&:hover': {
      boxShadow: `${theme.palette.black.main} 0px 0px 0px 0.06em`,
    },
    '&:active, &:focus': {
      boxShadow: `${theme.palette.blue.dark} 0px 0px 0px 0.11em`,
    },
    ...(disabled && {
      color: theme.palette.text.disabled,
      cursor: 'default',
      '&:hover': {
        boxShadow: `${theme.palette.action.disabled} 0px 0px 0px 0.06em`,
      },
      '&:active, &:focus': {
        boxShadow: `${theme.palette.action.disabled} 0px 0px 0px 0.06em`,
      },
    }),
  },
  ...(toolbarItems &&
    !toolbarItems.includes('bold') && {
      '& [aria-label="Bold (⌘B)"]': {
        display: 'none',
      },
    }),
  ...(toolbarItems &&
    !toolbarItems.includes('italic') && {
      '& [aria-label="Italic (⌘I)"]': {
        display: 'none',
      },
    }),
  ...(toolbarItems &&
    !toolbarItems.includes('underline') && {
      '& [aria-label="Underline (⌘U)"]': {
        display: 'none',
      },
    }),
  ...(toolbarItems &&
    !toolbarItems.length && {
      '& .remirror-editor-wrapper': {
        paddingTop: 0,
      },
    }),
}));
